import {
    ChevronLeftIcon,
    ChevronRightIcon,
} from '@heroicons/react/20/solid'
import { useEffect, useState } from "react";
import Parse from '../api/config';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

// Local YYYY-MM-DD. toISOString() converts to UTC first and can land a date on
// the wrong day.
const toISODate = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export const Calendar = ({ viewType = 'month' }) => {
    const today = new Date();
    const [selectedDate, setSelectedDate] = useState(today);
    const [month, setMonth] = useState(today.getMonth());
    const [year, setYear] = useState(today.getFullYear());
    const [days, setDays] = useState([]);
    const [approvedEvents, setApprovedEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [calendarView, setCalendarView] = useState(viewType);

    // Events are coloured by where they stand, using the same palette as the
    // status badges everywhere else. Department is shown as text, not colour.
    const getEventChip = (event) => {
        if (event.isSaveTheDate) return 'border border-dashed border-neutral-300 bg-neutral-50 text-neutral-600';
        if (event.isPending) return 'border border-amber-200 bg-amber-50 text-amber-900';
        return 'border border-emerald-200 bg-emerald-50 text-emerald-900';
    };

    useEffect(() => {
        fetchAllEvents();
    }, []);

    useEffect(() => {
        // Generate days for the displayed month
        generateDaysForMonth(month, year);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month, year, approvedEvents]);

    const fetchAllEvents = async () => {
        setLoading(true);
        try {
            // Query for fully approved forms
            const approvedQuery = new Parse.Query('Form');
            approvedQuery.equalTo('isFullyApproved', true);
            approvedQuery.notEqualTo('isDenied', true);
            
            // Query for pending forms (submitted but not fully approved yet)
            const pendingQuery = new Parse.Query('Form');
            pendingQuery.equalTo('isDraft', false); // Not a draft (has been submitted)
            pendingQuery.notEqualTo('isFullyApproved', true); // Not fully approved yet
            pendingQuery.notEqualTo('isDenied', true); // Not denied
            
            // Query for Save the Date requests
            const saveTheDateQuery = new Parse.Query('Form');
            saveTheDateQuery.equalTo('isSaveTheDate', true); 
            saveTheDateQuery.notEqualTo('isDenied', true);
            
            // Run all queries in parallel
            const [approvedForms, pendingForms, saveTheDateForms] = await Promise.all([
                approvedQuery.find(),
                pendingQuery.find(),
                saveTheDateQuery.find()
            ]);
            
            // A recurring event is ONE request carrying every date it happens
            // on, so each form expands into an entry per occurrence. Older
            // forms have no occurrenceDates and fall back to their single date.
            const expand = (forms, { isPending, isSaveTheDate, fallbackName }) =>
                forms.flatMap(form => {
                    const formData = form.toJSON();
                    const dates = (Array.isArray(formData.occurrenceDates) && formData.occurrenceDates.length)
                        ? formData.occurrenceDates
                        : (formData.dayDateOfEvent ? [formData.dayDateOfEvent] : []);

                    return dates.map(dateString => ({
                        // One form can appear on many days; keep the keys unique.
                        id: `${form.id}#${dateString}`,
                        formId: form.id,
                        name: formData.eventName || fallbackName,
                        department: formData.department || 'Other',
                        // dateString is already YYYY-MM-DD. Round-tripping it
                        // through toISOString() would shift it by a day.
                        dateString,
                        date: new Date(`${dateString}T00:00:00`),
                        location: formData.locationRequested || formData.location || 'No location specified',
                        startTime: formData.startTime || '00:00',
                        endTime: formData.endTime || '23:59',
                        occurrenceCount: dates.length,
                        isPending,
                        isSaveTheDate
                    }));
                });

            const approvedEvents = expand(approvedForms, {
                isPending: false, isSaveTheDate: false, fallbackName: 'Unnamed Event'
            });
            const pendingEvents = expand(pendingForms, {
                isPending: true, isSaveTheDate: false, fallbackName: 'Unnamed Event (Pending)'
            });
            const saveTheDateEvents = expand(saveTheDateForms, {
                isPending: true, isSaveTheDate: true, fallbackName: 'Unnamed Save the Date'
            });

            setApprovedEvents([...approvedEvents, ...pendingEvents, ...saveTheDateEvents]);
        } catch (error) {
            console.error('Error fetching events:', error);
        } finally {
            setLoading(false);
        }
    };

    const generateDaysForMonth = (month, year) => {
        const firstDayOfMonth = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const startDay = firstDayOfMonth.getDay();
        const daysArray = [];

        // Add days from previous month to fill the first week
        const prevMonth = month === 0 ? 11 : month - 1;
        const prevMonthYear = month === 0 ? year - 1 : year;
        const daysInPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate();
        
        for (let i = 0; i < startDay; i++) {
            const day = daysInPrevMonth - startDay + i + 1;
            const dateStr = `${prevMonthYear}-${(prevMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
            daysArray.push({
                date: dateStr,
                isCurrentMonth: false,
                isToday: false,
                day,
                events: getEventsForDate(dateStr)
            });
        }

        // Add days for current month
        for (let i = 1; i <= daysInMonth; i++) {
            const date = new Date(year, month, i);
            const dateStr = `${year}-${(month + 1).toString().padStart(2, '0')}-${i.toString().padStart(2, '0')}`;
            const isToday = date.toDateString() === today.toDateString();
            
            daysArray.push({
                date: dateStr,
                isCurrentMonth: true,
                isToday,
                day: i,
                events: getEventsForDate(dateStr)
            });
        }

        // Add days from next month to complete the last week
        const daysToAdd = 42 - daysArray.length; // 6 rows of 7 days
        const nextMonth = month === 11 ? 0 : month + 1;
        const nextMonthYear = month === 11 ? year + 1 : year;
        
        for (let i = 1; i <= daysToAdd; i++) {
            const dateStr = `${nextMonthYear}-${(nextMonth + 1).toString().padStart(2, '0')}-${i.toString().padStart(2, '0')}`;
            daysArray.push({
                date: dateStr,
                isCurrentMonth: false,
                isToday: false,
                day: i,
                events: getEventsForDate(dateStr)
            });
        }

        setDays(daysArray);
    };

    // Get events for a specific date
    const getEventsForDate = (dateString) => {
        return approvedEvents.filter(event => event.dateString === dateString);
    };

    const handlePreviousMonth = () => {
        if (month === 0) {
            setMonth(11);
            setYear(year - 1);
        } else {
            setMonth(month - 1);
        }
    };

    const handleNextMonth = () => {
        if (month === 11) {
            setMonth(0);
            setYear(year + 1);
        } else {
            setMonth(month + 1);
        }
    };

    // Get current week days
    const getCurrentWeekDays = () => {
        const currentDay = new Date(selectedDate);
        const day = currentDay.getDay(); // 0 = Sunday, 6 = Saturday
        const weekStart = new Date(selectedDate);
        weekStart.setDate(currentDay.getDate() - day); // Set to Sunday
        
        const weekDays = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(weekStart);
            date.setDate(weekStart.getDate() + i);
            const dateStr = toISODate(date);
            weekDays.push({
                date: dateStr,
                day: date.getDate(),
                isToday: date.toDateString() === today.toDateString(),
                dayName: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()],
                events: getEventsForDate(dateStr)
            });
        }
        return weekDays;
    };

    const handlePreviousWeek = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(selectedDate.getDate() - 7);
        setSelectedDate(newDate);
    };

    const handleNextWeek = () => {
        const newDate = new Date(selectedDate);
        newDate.setDate(selectedDate.getDate() + 7);
        setSelectedDate(newDate);
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        
        // Handle different time formats
        let hours, minutes, ampm;
        
        // If it's already in HH:MM format
        if (timeString.includes(':')) {
            [hours, minutes] = timeString.split(':');
            hours = parseInt(hours, 10);
            ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12; // Convert to 12-hour format
            return `${hours}:${minutes} ${ampm}`;
        }
        
        return timeString;
    };

    const monthName = new Date(year, month).toLocaleString('default', { month: 'long' });

    if (loading) {
        return <div className="p-4 text-center">Loading calendar events...</div>;
    }

    return (
        <div className="lg:flex lg:h-full lg:flex-col">
            <header className="flex flex-col border-b border-gray-200 px-6 py-4 lg:flex-none">
                <div className="flex items-center justify-between">
                    <h1 className="text-base font-semibold leading-6 text-gray-900">
                        <time dateTime={`${year}-${month + 1}`}>{`${monthName} ${year}`}</time>
                    </h1>
                    <div className="flex items-center space-x-4">
                        <div className="flex space-x-1 rounded-md shadow-sm">
                            <button
                                type="button"
                                onClick={() => setCalendarView('month')}
                                className={classNames(
                                    'relative inline-flex items-center rounded-l-md px-3 py-2 text-sm font-semibold',
                                    calendarView === 'month' 
                                        ? 'bg-brand-700 text-white hover:bg-brand-700' 
                                        : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                                )}
                            >
                                Month
                            </button>
                            <button
                                type="button"
                                onClick={() => setCalendarView('week')}
                                className={classNames(
                                    'relative -ml-px inline-flex items-center rounded-r-md px-3 py-2 text-sm font-semibold',
                                    calendarView === 'week' 
                                        ? 'bg-brand-700 text-white hover:bg-brand-700' 
                                        : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                                )}
                            >
                                Week
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Legend */}
                <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center text-xs text-gray-600">
                        <span>Event Status:</span>
                        <div className="flex items-center ml-4">
                            <div className="w-3.5 h-3.5 rounded-sm mr-1.5 border border-emerald-200 bg-emerald-50"></div>
                            <span className="mr-4">Approved</span>

                            <div className="w-3.5 h-3.5 rounded-sm mr-1.5 border border-amber-200 bg-amber-50"></div>
                            <span className="mr-4">Awaiting approval</span>

                            <div className="w-3.5 h-3.5 rounded-sm mr-1.5 border border-dashed border-neutral-300 bg-neutral-50"></div>
                            <span>Save the date</span>
                        </div>
                    </div>
                    
                    <div className="flex space-x-2">
                        {calendarView === 'month' ? (
                            <>
                                <button
                                    type="button"
                                    onClick={handlePreviousMonth}
                                    className="flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-400 hover:text-gray-500 focus:relative"
                                >
                                    <span className="sr-only">Previous month</span>
                                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNextMonth}
                                    className="flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-400 hover:text-gray-500 focus:relative"
                                >
                                    <span className="sr-only">Next month</span>
                                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={handlePreviousWeek}
                                    className="flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-400 hover:text-gray-500 focus:relative"
                                >
                                    <span className="sr-only">Previous week</span>
                                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    onClick={handleNextWeek}
                                    className="flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-gray-400 hover:text-gray-500 focus:relative"
                                >
                                    <span className="sr-only">Next week</span>
                                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </header>
            
            {calendarView === 'month' ? (
                // Month View
                <div className="shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col">
                    <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none">
                        <div className="bg-white py-2">Sun</div>
                        <div className="bg-white py-2">Mon</div>
                        <div className="bg-white py-2">Tue</div>
                        <div className="bg-white py-2">Wed</div>
                        <div className="bg-white py-2">Thu</div>
                        <div className="bg-white py-2">Fri</div>
                        <div className="bg-white py-2">Sat</div>
                    </div>
                    <div className="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
                        <div className="w-full grid grid-cols-7 grid-rows-6 gap-px">
                            {days.map((day) => (
                                <div
                                    key={day.date}
                                    className={classNames(
                                        day.isCurrentMonth ? 'bg-white' : 'bg-gray-50 text-gray-500',
                                        'relative min-h-28 px-3 py-2'
                                    )}
                                >
                                    <time
                                        dateTime={day.date}
                                        className={classNames(
                                            'flex h-6 w-6 items-center justify-center rounded-full',
                                            day.isToday ? 'bg-brand-700 font-semibold text-white' : undefined
                                        )}
                                    >
                                        {day.day}
                                    </time>
                                    {day.events.length > 0 && (
                                        <ol className="mt-2">
                                            {day.events.slice(0, 3).map((event) => (
                                                <li key={event.id} className="mb-1">
                                                    <div className={classNames(
                                                        "group flex flex-col rounded-lg p-1",
                                                        getEventChip(event)
                                                    )}>
                                                        <p className="truncate font-medium group-hover:text-brand-700">
                                                            {event.name}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {event.location}
                                                        </p>
                                                        {event.isPending && !event.isSaveTheDate && (
                                                            <p className="text-xs italic opacity-75 mt-1">Awaiting approval</p>
                                                        )}
                                                        {event.isSaveTheDate && (
                                                            <p className="text-xs italic opacity-75 mt-1">Save the date</p>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                            {day.events.length > 3 && (
                                                <li className="text-xs text-gray-500 mt-1">
                                                    + {day.events.length - 3} more
                                                </li>
                                            )}
                                        </ol>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                // Week View
                <div className="shadow ring-1 ring-black ring-opacity-5 lg:flex lg:flex-auto lg:flex-col">
                    <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs font-semibold leading-6 text-gray-700 lg:flex-none">
                        {getCurrentWeekDays().map((day) => (
                            <div 
                                key={day.date} 
                                className={classNames(
                                    "bg-white py-2",
                                    day.isToday ? "text-brand-700" : ""
                                )}
                            >
                                {day.dayName.substring(0, 3)}
                            </div>
                        ))}
                    </div>
                    <div className="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto">
                        <div className="w-full grid grid-cols-7 gap-px">
                            {getCurrentWeekDays().map((day) => (
                                <div
                                    key={day.date}
                                    className={classNames(
                                        'bg-white relative min-h-80 p-2 overflow-y-auto',
                                        day.isToday ? 'bg-brand-50' : ''
                                    )}
                                >
                                    <div className="sticky top-0 z-10 bg-white pb-2">
                                        <time
                                            dateTime={day.date}
                                            className={classNames(
                                                'flex h-6 w-6 items-center justify-center rounded-full mb-2',
                                                day.isToday ? 'bg-brand-700 font-semibold text-white' : 'font-semibold'
                                            )}
                                        >
                                            {day.day}
                                        </time>
                                    </div>
                                    
                                    {day.events.length > 0 ? (
                                        <ol className="space-y-2">
                                            {day.events.map((event) => (
                                                <li key={event.id}>
                                                    <div className={classNames(
                                                        "group flex flex-col rounded-lg p-2",
                                                        getEventChip(event)
                                                    )}>
                                                        <p className="font-medium group-hover:text-brand-700">
                                                            {event.name}
                                                        </p>
                                                        <p className="text-xs text-gray-600 mt-1">
                                                            {event.location}
                                                        </p>
                                                        <div className="flex justify-between text-xs mt-1">
                                                            <span>{formatTime(event.startTime)}</span>
                                                            {event.endTime && (
                                                                <span>- {formatTime(event.endTime)}</span>
                                                            )}
                                                        </div>
                                                        {event.isPending && !event.isSaveTheDate && (
                                                            <p className="text-xs italic opacity-75 mt-1">Awaiting approval</p>
                                                        )}
                                                        {event.isSaveTheDate && (
                                                            <p className="text-xs italic opacity-75 mt-1">Save the date</p>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ol>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-gray-400">
                                            No events
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};